import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AppLayout } from './components/layout/AppLayout';
import { AuthLayout } from './components/layout/AuthLayout';
import { DemoShell } from './components/layout/DemoShell';
import { RequireAuth } from './auth/RequireAuth';
import { LoadingState } from './components/LoadingState';
import { AccessDeniedPage, NotFoundPage } from './pages/ErrorPages';
import { LoginPage } from './pages/LoginPage';

// Every page but sign-in and the error pages is loaded when it is first opened, so the first
// screen does not wait for the whole portal (the single bundle passed Vite's 500 kB warning in
// October 2026). The layouts wrap their <Outlet /> in <Suspense>, so the sidebar or the sign-in
// frame stays on screen while a page loads; the boundary around <Routes> covers the pages with no
// layout (landing, docs, signup).
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })));
const AuditLogsPage = lazy(() => import('./pages/AuditLogsPage').then((m) => ({ default: m.AuditLogsPage })));
const ApiLogsPage = lazy(() => import('./pages/ApiLogsPage').then((m) => ({ default: m.ApiLogsPage })));
const ApiLogDetailPage = lazy(() => import('./pages/ApiLogDetailPage').then((m) => ({ default: m.ApiLogDetailPage })));
const CompliancePage = lazy(() => import('./pages/CompliancePage').then((m) => ({ default: m.CompliancePage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const DemoPage = lazy(() => import('./pages/DemoPage').then((m) => ({ default: m.DemoPage })));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage').then((m) => ({ default: m.DocumentsPage })));
const FraudAlertsPage = lazy(() => import('./pages/FraudAlertsPage').then((m) => ({ default: m.FraudAlertsPage })));
const FraudAlertDetailPage = lazy(() => import('./pages/FraudAlertDetailPage').then((m) => ({ default: m.FraudAlertDetailPage })));
const FraudPage = lazy(() => import('./pages/FraudPage').then((m) => ({ default: m.FraudPage })));
const LandingPage = lazy(() => import('./pages/LandingPage').then((m) => ({ default: m.LandingPage })));
const DocsApiPage = lazy(() => import('./pages/docs/DocsApiPage').then((m) => ({ default: m.DocsApiPage })));
const DocsOverviewPage = lazy(() => import('./pages/docs/DocsOverviewPage').then((m) => ({ default: m.DocsOverviewPage })));
const DocsSdkPage = lazy(() => import('./pages/docs/DocsSdkPage').then((m) => ({ default: m.DocsSdkPage })));
const ForgotPasswordPage = lazy(() => import('./pages/PasswordResetPages').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/PasswordResetPages').then((m) => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => import('./pages/PasswordResetPages').then((m) => ({ default: m.VerifyEmailPage })));
const SignupCancelPage = lazy(() => import('./pages/SignupPage').then((m) => ({ default: m.SignupCancelPage })));
const SignupPage = lazy(() => import('./pages/SignupPage').then((m) => ({ default: m.SignupPage })));
const SignupSuccessPage = lazy(() => import('./pages/SignupPage').then((m) => ({ default: m.SignupSuccessPage })));
const AdminPlansPage = lazy(() => import('./pages/AdminPlansPage').then((m) => ({ default: m.AdminPlansPage })));
const AdminSupportPage = lazy(() => import('./pages/AdminSupportPage').then((m) => ({ default: m.AdminSupportPage })));
const NewSupportTicketPage = lazy(() => import('./pages/NewSupportTicketPage').then((m) => ({ default: m.NewSupportTicketPage })));
const SupportPage = lazy(() => import('./pages/SupportPage').then((m) => ({ default: m.SupportPage })));
const SupportTicketPage = lazy(() => import('./pages/SupportTicketPage').then((m) => ({ default: m.SupportTicketPage })));
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage })));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage').then((m) => ({ default: m.ChangePasswordPage })));
const ClientDetailPage = lazy(() => import('./pages/ClientDetailPage').then((m) => ({ default: m.ClientDetailPage })));
const ClientsPage = lazy(() => import('./pages/ClientsPage').then((m) => ({ default: m.ClientsPage })));
const AdminSettingsPage = lazy(() => import('./pages/AdminSettingsPage').then((m) => ({ default: m.AdminSettingsPage })));
const BillingPage = lazy(() => import('./pages/BillingPage').then((m) => ({ default: m.BillingPage })));
const CompanyPage = lazy(() => import('./pages/CompanyPage').then((m) => ({ default: m.CompanyPage })));
const MyProfilePage = lazy(() => import('./pages/MyProfilePage').then((m) => ({ default: m.MyProfilePage })));
const CreditsPage = lazy(() => import('./pages/CreditsPage').then((m) => ({ default: m.CreditsPage })));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const IntegrationPage = lazy(() => import('./pages/IntegrationPage').then((m) => ({ default: m.IntegrationPage })));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage').then((m) => ({ default: m.UserProfilePage })));
const UsersPage = lazy(() => import('./pages/UsersPage').then((m) => ({ default: m.UsersPage })));
const VerificationDetailPage = lazy(() => import('./pages/VerificationDetailPage').then((m) => ({ default: m.VerificationDetailPage })));
const VerificationReviewPage = lazy(() => import('./pages/VerificationReviewPage').then((m) => ({ default: m.VerificationReviewPage })));
const VerificationRulesPage = lazy(() => import('./pages/VerificationRulesPage').then((m) => ({ default: m.VerificationRulesPage })));
const FraudRulesPage = lazy(() => import('./pages/FraudRulesPage').then((m) => ({ default: m.FraudRulesPage })));
const BrandingPage = lazy(() => import('./pages/BrandingPage').then((m) => ({ default: m.BrandingPage })));
const VerificationsPage = lazy(() => import('./pages/VerificationsPage').then((m) => ({ default: m.VerificationsPage })));

/**
 * Route table. Paths are the kebab-case equivalents of the Razor page routes
 * (/Verifications/Detail/12 becomes /verifications/12), and the guard mirrors the
 * AuthorizeFolder / AuthorizePage conventions from IdVerify.Web's Program.cs.
 */
export function App() {
  return (
    <Suspense fallback={<LoadingState />}>
      <Routes>
        {/* Public marketing page. Outside AuthLayout because it brings its own navbar and footer,
            and outside RequireAuth because its whole purpose is to be seen by people without an
            account. It redirects signed-in visitors to the dashboard itself, which is what
            IndexModel.OnGetAsync does in the Razor portal. */}
        <Route path="/" element={<LandingPage />} />

        {/* Public developer documentation - the Razor /Docs pages. Anonymous like the landing page,
            but signed-in users may read it too (no redirect). */}
        <Route path="/docs" element={<DocsOverviewPage />} />
        <Route path="/docs/api" element={<DocsApiPage />} />
        <Route path="/docs/sdks" element={<DocsSdkPage />} />

        {/* Live demo - public page; both tabs (verification demo, Try Extractor) need a signed-in
            user and show a sign-up prompt otherwise. DemoShell picks the app shell or the
            marketing chrome, as the Razor page picks _Layout or _LandingLayout. */}
        <Route element={<DemoShell />}>
          <Route path="/demo" element={<DemoPage />} />
        </Route>

        {/* Self-service company registration - the Razor /Signup pages. Anonymous, with their own
            navbar like the landing page. /signup/success and /signup/cancel are Stripe's return
            URLs for a paid signup. */}
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/signup/success" element={<SignupSuccessPage />} />
        <Route path="/signup/cancel" element={<SignupCancelPage />} />

        {/* Anonymous */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/access-denied" element={<AccessDeniedPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Authenticated, but outside the app shell.
            The change-password screen uses the login layout: an account that has been forced here
            has no business seeing a sidebar full of links it is not allowed to follow. It sits
            inside RequireAuth because changing a password requires being signed in. */}
        <Route element={<RequireAuth />}>
          <Route element={<AuthLayout />}>
            <Route path="/change-password" element={<ChangePasswordPage />} />
          </Route>
        </Route>

        {/* Authenticated */}
        <Route element={<RequireAuth />}>
          <Route element={<AppLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />

            <Route path="/verifications" element={<VerificationsPage />} />
            <Route path="/verifications/:id" element={<VerificationDetailPage />} />
            <Route path="/verifications/:id/review" element={<VerificationReviewPage />} />

            <Route path="/documents" element={<DocumentsPage />} />

            <Route path="/fraud" element={<FraudPage />} />
            <Route path="/fraud/alerts" element={<FraudAlertsPage />} />
            <Route path="/fraud/alerts/:id" element={<FraudAlertDetailPage />} />
            <Route path="/audit-logs" element={<AuditLogsPage />} />
            <Route path="/api-logs" element={<ApiLogsPage />} />
            <Route path="/api-logs/:id" element={<ApiLogDetailPage />} />
            <Route path="/reports/analytics" element={<AnalyticsPage />} />
            <Route path="/reports/compliance" element={<CompliancePage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/users/:id" element={<UserProfilePage />} />

            {/* Clients list and detail are authenticated-only, matching IdVerify.Web's
                conventions - only Create/Edit/Billing were SuperAdmin-gated there. The sidebar
                hides the link for client users, but a client badge on the Users page still
                links here, so gating these routes by role would be a behaviour change. The
                admin-only pieces are folded into these two routes as dialogs and are gated by
                the permissions the API returns with the client. */}
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/clients/:id" element={<ClientDetailPage />} />

            <Route path="/account/profile" element={<MyProfilePage />} />
            <Route path="/account/company" element={<CompanyPage />} />
            <Route path="/account/credits" element={<CreditsPage />} />
            <Route path="/account/billing" element={<BillingPage />} />
            <Route path="/account/notifications" element={<NotificationsPage />} />
            <Route path="/account/integration" element={<IntegrationPage />} />
            <Route path="/account/api-keys" element={<ApiKeysPage />} />
            <Route path="/account/verification-rules" element={<VerificationRulesPage />} />
            <Route path="/account/fraud-rules" element={<FraudRulesPage />} />
            <Route path="/account/branding" element={<BrandingPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/support/new" element={<NewSupportTicketPage />} />
            <Route path="/support/:id" element={<SupportTicketPage />} />
          </Route>
        </Route>

        {/* Authenticated, SuperAdmin only */}
        <Route element={<RequireAuth roles={['SuperAdmin']} />}>
          <Route element={<AppLayout />}>
            <Route path="/admin/plans" element={<AdminPlansPage />} />
            <Route path="/admin/settings" element={<AdminSettingsPage />} />
            <Route path="/admin/support" element={<AdminSupportPage />} />
            <Route path="/admin/support/:id" element={<SupportTicketPage />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
